import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, type Me } from "./api";

type AuthState =
  | { status: "loading" }
  | { status: "authenticated"; me: Me }
  | { status: "anonymous" };

type AuthContextValue = AuthState & {
  setMe: (me: Me) => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  // One call on mount decides whether the cookie we may already hold is still
  // good. Until it answers, nothing renders — otherwise a returning user sees
  // the login page flash before being bounced to the dashboard.
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => !cancelled && setState({ status: "authenticated", me }))
      .catch(() => !cancelled && setState({ status: "anonymous" }));
    return () => {
      cancelled = true;
    };
  }, []);

  const value: AuthContextValue = {
    ...state,
    setMe: (me) => setState({ status: "authenticated", me }),
    signOut: async () => {
      try {
        await api.logout();
      } finally {
        // Local state clears even if the call failed — the alternative is a
        // user who pressed sign out and is still looking at their data.
        setState({ status: "anonymous" });
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
